import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Package, FileText, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { convertGoogleDriveLink } from '../utils/imageUtils';

const DashboardPage = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, customOrdersRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/custom-orders`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOrders(ordersRes.data);
      setCustomOrders(customOrdersRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getCustomOrderStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'submitted':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen py-12" data-testid="dashboard-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Dashboard</h1>
        <p className="text-lg text-muted-foreground mb-12">Welcome back, {user?.business_name}</p>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-8 border-b border-border">
          <button
            onClick={() => setActiveTab('orders')}
            className={`pb-4 px-2 font-medium transition-colors border-b-2 ${
              activeTab === 'orders'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="orders-tab"
          >
            <Package size={20} className="inline mr-2" />
            Orders ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`pb-4 px-2 font-medium transition-colors border-b-2 ${
              activeTab === 'custom'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="custom-orders-tab"
          >
            <FileText size={20} className="inline mr-2" />
            Custom Requests ({customOrders.length})
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-4 px-2 font-medium transition-colors border-b-2 ${
              activeTab === 'profile'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="profile-tab"
          >
            <User size={20} className="inline mr-2" />
            Profile
          </button>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="orders-content"
          >
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg text-muted-foreground">No orders yet</p>
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => {
                  return (
                    <div key={order.id} className="bg-white border border-border rounded-lg overflow-hidden shadow-sm" data-testid={`order-${order.id}`}>
                      {/* Order Header */}
                      <div className="bg-gray-50 px-6 py-4 border-b border-border">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-heading font-bold text-gray-900">Order #{order.order_number}</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              Placed on {new Date(order.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Order Items */}
                      <div className="p-6">
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Items</h4>
                        <div className="space-y-4">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg" data-testid={`order-item-${idx}`}>
                              <div className="w-20 h-20 flex-shrink-0 border border-border rounded-lg overflow-hidden bg-white">
                                {item.image ? (
                                  <img
                                    src={convertGoogleDriveLink(item.image)}
                                    alt={item.product_name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Package size={28} className="text-muted-foreground/40" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900">{item.product_name}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {item.variant_sku && <span className="mr-2">Design No: {item.variant_sku}</span>}
                                  <span className="font-medium">Qty: {item.quantity}</span>
                                </p>
                              </div>
                              <p className="text-lg font-bold text-primary flex-shrink-0">
                                ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                              </p>
                            </div>
                          ))}
                        </div>
                        
                        {/* Order Summary */}
                        <div className="mt-6 pt-4 border-t border-border">
                          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Summary</h4>
                          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                            {order.subtotal > 0 && (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Subtotal</span>
                                  <span>₹{(order.subtotal || 0).toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">GST (18%)</span>
                                  <span>₹{(order.gst_amount || 0).toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Shipping</span>
                                  <span>₹{(order.shipping_charges || 0).toLocaleString('en-IN')}</span>
                                </div>
                              </>
                            )}
                            <div className="flex justify-between pt-3 border-t border-border">
                              <span className="text-lg font-bold">Grand Total</span>
                              <span className="text-xl font-bold text-primary">₹{order.total_amount.toLocaleString('en-IN')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* Custom Orders Tab */}
        {activeTab === 'custom' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="custom-orders-content"
          >
            {customOrders.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg text-muted-foreground">No custom order requests yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {customOrders.map((customOrder) => (
                  <div key={customOrder.id} className="bg-white border border-border p-6 rounded-lg" data-testid={`custom-order-${customOrder.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-heading font-semibold mb-1">Custom Request</h3>
                        <p className="text-sm text-muted-foreground">
                          {new Date(customOrder.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-3 py-1 text-sm font-medium rounded ${getCustomOrderStatusColor(customOrder.status)} mt-2 md:mt-0`}>
                        {customOrder.status}
                      </span>
                    </div>
                    <div className="border-t border-border pt-4">
                      <p className="text-muted-foreground mb-2">{customOrder.design_description}</p>
                      <p className="text-sm">Quantity: {customOrder.quantity}</p>
                      <p className="text-sm">Budget: {customOrder.budget_range}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="profile-content"
          >
            <div className="bg-white border border-border p-6 rounded-lg">
              <h2 className="text-2xl font-heading font-semibold mb-6">Business Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Business Name</p>
                  <p className="font-semibold text-lg">{user?.business_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Contact Person</p>
                  <p className="font-semibold text-lg">{user?.contact_person}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Email</p>
                  <p className="font-semibold text-lg">{user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Phone</p>
                  <p className="font-semibold text-lg">{user?.phone}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">Address</p>
                  <p className="font-semibold">{user?.address}</p>
                </div>
                {user?.gst_number && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">GST Number</p>
                    <p className="font-semibold font-mono bg-gray-100 px-3 py-2 rounded inline-block">{user.gst_number}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
